import type { DurationLikeObject, DurationUnits, Zone } from 'luxon';
import { DateTime as LuxonDateTime, IANAZone } from 'luxon';

import { Clock } from './clock';
import { Duration } from './duration';
import { DayOfWeek, TimeOfDay } from './time-of-day';

export class DateTime {
	private static workspaceTimeZoneResolver: (() => string | null | undefined) | null = null;

	/**
	 * Get the current computer timezone
	 * Being explicit on the timezone prevents issues with how libraries handle timezones.
	 */
	static getTimeZone(): string {
		// We are using the Intl API to get the timezone of the computer
		// This "should" be syced with the timezone of user profile
		// By explicity setting the timezone we prevent issues with how libraries determine "local"
		return Intl.DateTimeFormat().resolvedOptions().timeZone;
	}

	/**
	 * Teach DateTime how to find the timezone the workspace operates in. Registered once during
	 * app bootstrap; it is read on every call so a workspace switch or a timezone change in
	 * settings is picked up without a reload. Pass `null` to fall back to the browser's timezone.
	 *
	 * Used when converting opening-hours minutes (workspace wall-clock) into user wall-clock
	 * for floating booking dates — see {@link fromWorkspaceWallClock}.
	 */
	static setWorkspaceTimeZoneResolver(resolver: (() => string | null | undefined) | null): void {
		DateTime.workspaceTimeZoneResolver = resolver;
	}

	/**
	 * The workspace's timezone, falling back to the browser's when unresolved or not a valid IANA zone.
	 */
	static getWorkspaceTimeZone(): string {
		const zone = DateTime.workspaceTimeZoneResolver?.();
		if (zone && IANAZone.isValidZone(zone)) {
			return zone;
		}
		return DateTime.getTimeZone();
	}

	/**
	 * Interpret opening-hours minutes as wall-clock in the workspace zone, convert that instant
	 * into the computer/user zone, then keep the resulting HH:mm on the **selected** calendar day.
	 *
	 * Day clamping matches month/day picker UX (operator clicked a Y-M-D) and floating booking
	 * storage. Minutes may be `1440` (midnight close) — treated as next-day 00:00 in the
	 * workspace before conversion.
	 *
	 * When the converted instant falls on a **later** user calendar day:
	 * - `boundary: 'close'` (closes, including UI all-day `1439` and midnight `1440`) → end of
	 *   selected day. Pinning early-morning HH:mm would invert open/close under west-of-user offsets.
	 * - `boundary: 'open'` → clamp converted HH:mm onto the selected day (do not snap to 23:59).
	 *
	 * Default boundary: `'close'` when `minutesSinceMidnight >= 1440`, otherwise `'open'`.
	 * Call sites for business-day end should pass `boundary: 'close'` explicitly.
	 *
	 * python-api: opening hours are workspace minutes; booking datetimes are user floating values.
	 */
	static fromWorkspaceWallClock(
		day: DateTime | { year: number; month: number; day: number },
		minutesSinceMidnight: number,
		options?: { fromZone?: string; boundary?: 'open' | 'close' }
	): DateTime {
		const calendarDay = { year: day.year, month: day.month, day: day.day };
		const fromZone = options?.fromZone ?? DateTime.getWorkspaceTimeZone();
		const boundary = options?.boundary ?? (minutesSinceMidnight >= 1440 ? 'close' : 'open');

		// 1440 = end-of-day midnight (next calendar day 00:00 in the workspace).
		let dayOffset = 0;
		let minutes = minutesSinceMidnight;
		if (minutes >= 1440) {
			dayOffset = Math.floor(minutes / 1440);
			minutes = minutes % 1440;
		} else if (minutes < 0) {
			minutes = 0;
		}

		const hour = Math.floor(minutes / 60);
		const minute = minutes % 60;
		const toZone = DateTime.getTimeZone();

		const workspaceInstant = LuxonDateTime.fromObject(
			{
				year: calendarDay.year,
				month: calendarDay.month,
				day: calendarDay.day,
				hour,
				minute,
				second: 0,
			},
			{ zone: fromZone }
		).plus({ days: dayOffset });

		const userWallClock = workspaceInstant.setZone(toZone);

		const landsAfterSelectedDay =
			userWallClock.year > calendarDay.year ||
			(userWallClock.year === calendarDay.year && userWallClock.month > calendarDay.month) ||
			(userWallClock.year === calendarDay.year &&
				userWallClock.month === calendarDay.month &&
				userWallClock.day > calendarDay.day);

		// Close next-day: pinning early HH:mm onto the selected day inverts open/close.
		if (landsAfterSelectedDay && boundary === 'close') {
			return new DateTime(
				LuxonDateTime.fromObject(
					{
						year: calendarDay.year,
						month: calendarDay.month,
						day: calendarDay.day,
					},
					{ zone: toZone }
				).endOf('day')
			);
		}

		// Keep the selected calendar day; only the converted wall-clock time moves.
		return new DateTime(
			LuxonDateTime.fromObject(
				{
					year: calendarDay.year,
					month: calendarDay.month,
					day: calendarDay.day,
					hour: userWallClock.hour,
					minute: userWallClock.minute,
					second: 0,
				},
				{ zone: toZone }
			)
		);
	}

	/**
	 * Returns the given data as a DateTime object in UTC.
	 * @param date - a JavaScript Date object
	 * @param options.zone - the zone to place the DateTime into
	 */
	static fromDateAsUTC(date: Date, options?: { zone?: string | Zone }): DateTime {
		return new DateTime(LuxonDateTime.fromJSDate(date, options).setZone('utc'));
	}

	/**
	 * Returns the given data as a DateTime object in computer timezone
	 * @param date - a JavaScript Date object
	 * @param options.zone - the zone to place the DateTime into
	 */
	static fromDate(date: Date, options?: { zone?: string | Zone }): DateTime {
		return new DateTime(LuxonDateTime.fromJSDate(date, options).setZone(DateTime.getTimeZone()));
	}

	/**
	 * Resolve zoneless input in the computer zone. Luxon would otherwise use its system
	 * zone, which rounds offsets to whole minutes and so lands seconds off midnight for
	 * pre-standard-time dates. See `docs/layers/date-and-time.md`.
	 */
	private static parseOptions(options?: { zone?: string | Zone }): { zone: string | Zone } {
		return { zone: DateTime.getTimeZone(), ...options };
	}

	/**
	 * Returns the given data as a DateTime object in computer timezone
	 * @param text - a date string
	 * @param fmt - the format of the date string
	 * @param options.zone - the zone to place the DateTime into
	 */
	static fromDateString(text: string, fmt = 'yyyy-MM-dd', options?: { zone?: string | Zone }): DateTime {
		return new DateTime(LuxonDateTime.fromFormat(text, fmt, DateTime.parseOptions(options)));
	}

	/**
	 * Convert the given ISO string to a DateTime object in UTC.
	 * @param text - an ISO string
	 * @param options.zone - the zone to place the DateTime into
	 */
	static fromISOStringAsUTC(text: string, options?: { zone?: string | Zone }): DateTime {
		return new DateTime(LuxonDateTime.fromISO(text, DateTime.parseOptions(options)).setZone('utc'));
	}

	/**
	 * Convert the givent ISO string to a DateTime object in computer timezone.
	 * @param text - an ISO string
	 * @param options.zone - the zone to place the DateTime into
	 */
	static fromISOString(text: string, options?: { zone?: string | Zone }): DateTime {
		return new DateTime(
			LuxonDateTime.fromISO(text, DateTime.parseOptions(options)).setZone(DateTime.getTimeZone())
		);
	}

	/**
	 * Convert the givent date object to a DateTime object in computer timezone.
	 * @param date - a date object
	 * @param options.zone - the zone to place the DateTime into
	 */
	static fromObject(
		date: {
			year: number;
			month: number;
			date?: number;
			hours?: number;
			minutes?: number;
			seconds?: number;
		},
		options?: { zone?: string | Zone }
	): DateTime {
		return new DateTime(
			LuxonDateTime.fromObject(
				{
					year: date.year,
					month: date.month,
					day: date.date || 1,
					hour: date.hours || 0,
					minute: date.minutes || 0,
					second: date.seconds || 0,
				},
				DateTime.parseOptions(options)
			).setZone(DateTime.getTimeZone())
		);
	}

	static now(): DateTime {
		return DateTime.fromDate(Clock.now());
	}

	static max(...dateTimes: (DateTime | null | undefined)[]): DateTime {
		const filteredDates = dateTimes.filter((dt): dt is DateTime => dt != null).map((dt) => dt.date);
		const maxDate = LuxonDateTime.max(...filteredDates) as LuxonDateTime;
		return new DateTime(maxDate);
	}

	static min(...dateTimes: (DateTime | null | undefined)[]): DateTime {
		const filteredDates = dateTimes.filter((dt): dt is DateTime => dt != null).map((dt) => dt.date);
		const minDate = LuxonDateTime.min(...filteredDates) as LuxonDateTime;
		return new DateTime(minDate);
	}

	static isValidDateString(text: string, options?: { zone?: string | Zone }): boolean {
		return LuxonDateTime.fromISO(text, options).isValid;
	}

	static isISOString(value: unknown): boolean {
		if (typeof value === 'string' || value instanceof String) {
			// Regex for ISO 8601 (covers with/without milliseconds and with Z or offset)
			const isoRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;
			return isoRegex.test(value as string);
		}
		return false;
	}

	/**
	 * Strip the UTC designator from a datetime string.
	 *
	 * Primarily used to fix Python Dates from the API that do not have real timezone information:
	 * they are wall-clock values carrying a meaningless `Z` / `+00:00`. Dropping the designator
	 * keeps the wall-clock intact instead of having it shifted into the browser's zone on parse.
	 *
	 * Any sub-second precision is accepted and discarded. Genuine (non-UTC) offsets are left
	 * untouched — those carry real instant information and are not ours to reinterpret.
	 */
	static stripTimeZone(data: string): string {
		const utcWallClock = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})(?:\.\d+)?(?:Z|\+00:00)$/.exec(data);

		return utcWallClock ? utcWallClock[1] : data;
	}

	private constructor(private date: LuxonDateTime) {
		this.date = date;
	}

	getLuxonDate(): LuxonDateTime {
		return this.date;
	}

	get isValid(): boolean {
		return this.date.isValid;
	}

	get fullYear(): number {
		return this.date.year;
	}

	get year(): number {
		return this.date.year;
	}

	get month(): number {
		return this.date.month;
	}

	/** Day of the month (1–31). For the weekday, use `dayOfWeek`. */
	get day(): number {
		return this.date.day;
	}

	get ordinal(): number {
		return this.date.ordinal;
	}

	get weekday(): number {
		return this.date.weekday;
	}

	get dayOfWeek(): DayOfWeek {
		return DayOfWeek.fromISOWeekday(this.weekday);
	}

	get weekDayLong(): string {
		const weekDayLong = this.date.weekdayLong;

		if (!weekDayLong) {
			throw new Error('Invalid weekDayLong');
		}

		return weekDayLong;
	}

	get hours(): number {
		return this.date.hour;
	}

	get minutes(): number {
		return this.date.minute;
	}

	get seconds(): number {
		return this.date.second;
	}

	withTime(time: TimeOfDay): DateTime {
		return new DateTime(this.date.set({ hour: time.hours, minute: time.minutes, second: time.seconds }));
	}

	isBefore(date: DateTime): boolean {
		// https://moment.github.io/luxon/docs/manual/math.html
		return this.date < date.date;
	}

	isBeforeOrEqual(date: DateTime): boolean {
		return this.date <= date.date;
	}

	isAfter(date: DateTime): boolean {
		// https://moment.github.io/luxon/docs/manual/math.html
		return this.date > date.date;
	}

	isAfterOrEqual(date: DateTime): boolean {
		return this.date >= date.date;
	}

	isSameDate(date: DateTime): boolean {
		return this.date.equals(date.getLuxonDate());
	}

	isSameDay(date: DateTime): boolean {
		return this.date.year === date.year && this.date.ordinal === date.ordinal;
	}

	isPast(): boolean {
		return this.isBefore(DateTime.now());
	}

	isPastDay(): boolean {
		return this.isBefore(DateTime.now().startOfDay());
	}

	isFuture(): boolean {
		return this.isAfter(DateTime.now());
	}

	toISOString(): string {
		const isoString = this.date.toISO();

		if (!isoString) {
			throw new Error('Invalid ISO string');
		}

		return isoString;
	}

	/**
	 * Serializes the wall-clock time with a `Z` suffix but WITHOUT converting zones,
	 * i.e. a zoneless ("floating") instant. The backend treats booking times as the
	 * acting user's wall-clock, so a 14:00 user-local pick is sent as `...T14:00:00Z`.
	 */
	toFloatingISOString(): string {
		return this.withZone('utc', { keepLocalTime: true }).toISOString();
	}

	toUTC(): DateTime {
		return new DateTime(this.date.toUTC());
	}

	toISODateString(): string {
		const isoDateString = this.date.toISODate();

		if (!isoDateString) {
			throw new Error('Invalid ISO date string');
		}

		return isoDateString;
	}

	toDateString(): string {
		return this.date.toLocaleString({
			weekday: 'long',
			year: 'numeric',
			month: 'long',
			day: 'numeric',
		});
	}

	toDateTimeString(format = LuxonDateTime.DATETIME_MED): string {
		return this.date.toLocaleString(format);
	}

	toDate(): Date {
		return this.date.toJSDate();
	}

	toFormat(format: string): string {
		return this.date.toFormat(format);
	}

	toRelative(useHours = true): string | null {
		// Luxon's own base is a second "now". A few milliseconds later, an exact
		// hour or day truncates to the unit below. Use the app clock for both.
		const base = LuxonDateTime.fromJSDate(Clock.now(), { zone: this.date.zone });

		return useHours || !this.isWithin24Hours()
			? this.date.toRelative({ base })
			: this.date.toRelativeCalendar({ base });
	}

	toMillis(): number {
		return this.date.toMillis();
	}
	add(duration: DurationLikeObject): DateTime {
		return new DateTime(this.date.plus(duration));
	}

	addSeconds(seconds: number): DateTime {
		return this.add({ seconds });
	}

	addMinutes(minutes: number): DateTime {
		return this.add({ minutes });
	}

	addDays(days: number): DateTime {
		return this.add({ days });
	}

	subtract(duration: DurationLikeObject): DateTime {
		return new DateTime(this.date.minus(duration));
	}

	subtractDays(days: number): DateTime {
		return this.subtract({ days });
	}

	addMonths(months: number): DateTime {
		return this.add({ months });
	}

	addYears(years: number): DateTime {
		return this.add({ years });
	}

	startOfDay(): DateTime {
		return new DateTime(this.date.startOf('day'));
	}

	endOfDay(): DateTime {
		return new DateTime(this.date.endOf('day'));
	}

	startOfMonth(): DateTime {
		return new DateTime(this.date.startOf('month'));
	}

	endOfMonth(): DateTime {
		return new DateTime(this.date.endOf('month'));
	}

	roundToNearestMinutes(interval: number): DateTime {
		const minutes = this.date.minute + this.date.second / 60 + this.date.millisecond / 60000;
		const rounded = Math.ceil(minutes / interval) * interval;

		return new DateTime(this.date.set({ minute: rounded, second: 0, millisecond: 0 }));
	}

	valueOf(): number {
		return this.date.valueOf();
	}

	diff(otherDateTime: DateTime, unit?: DurationUnits, options?: { zone?: string | Zone }): Duration {
		const luxonDateTime = LuxonDateTime.fromISO(otherDateTime.toISOString(), options).setZone(
			DateTime.getTimeZone()
		);

		const diffISO = this.date.diff(luxonDateTime, unit).toISO();

		if (!diffISO) {
			throw new Error('Invalid diff');
		}

		return Duration.fromISO(diffISO);
	}

	isWithin24Hours(): boolean {
		const diffInDays = this.diff(DateTime.now()).as('days');
		return diffInDays < 1 && diffInDays > -1;
	}

	withZone(zone: string, options?: { keepLocalTime: boolean }): DateTime {
		return new DateTime(this.date.setZone(zone, options));
	}

	getQuarter(): number {
		return Math.floor((this.date.month - 1) / 3) + 1;
	}
}
